import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import classNames from "classnames";
import { useForm, SubmitHandler, Controller, useWatch } from "react-hook-form";
import {
    FmText,
    FmPageTitle,
    FmDescriptionSnippet,
    FmTextField,
    FmDatePicker,
    FmMapInput,
    FmAutocompleteInput,
    FmUnderlineNav,
    FmBlackPopup,
} from "../../../Components";
import Configs from "../../../Configs";
import { AgriSolutionState, GeneralState } from "../../../Redux";
import { BottomSection } from "../Components";
import { LOGGED_IN_ROUTES, YIELD_MAX, YIELD_MIN, BIG_MESURMENT_UNIT } from "../../../Constants";
import { useIsMobile, useTheme } from "../../../Hooks";
import { TimeUtils, ValidationUtils, CalculateUtils } from "../../../Utils";
import { FmAllModals } from "../../../Components/Modals";
import useStyles from "./CropStagePage.styles";
import { updateCropStateTemperature } from "../../../Redux/States/AgriSolution/AgriSolutionThunk";
import { FmCarbonFootprintBanner } from "../../../Components/FmCarbonFootprintBanner";
import Config from "../../../Configs";

interface IFormInputs {
    selectedCrop: string;
    expectedYield: number | undefined;
    plantingDate: string;
    finalHarvestDate: string;
    selectedLocationCountryCode: string;
}

const daysToJumpForFinalDate = 60;

const CropStagePage = () => {
    const classes = useStyles();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const history = useHistory();
    const isMobile = useIsMobile();
    const [showSnippet, setShowSnippet] = useState<boolean>(true);
    const { selectedCrop, expectedYield, metricUnit, plantingDate, finalHarvestDate, selectedLocationCountryCode, selectedLocationLat, selectedLocationLng } =
        useSelector(AgriSolutionState.Selectors.selectCropsState);
    const crops = useSelector(GeneralState.Selectors.selectCrops);
    const loading = useSelector(AgriSolutionState.Selectors.selectSaveInProgress);
    const { activeMetricMethod } = useSelector(GeneralState.Selectors.selectMetricMethods);
    const countriesWithHaifaProducts = useSelector(GeneralState.Selectors.selectCountriesWithProducts);
    const showMobileMap = useSelector(AgriSolutionState.Selectors.selectShowMobileMap);
    const greenBar = useSelector(GeneralState.Selectors.selectGreenBarState);

    const haveItemId = useSelector(AgriSolutionState.Selectors.selectItemId);

    const [lastKnowActiveMethod, setLastKnownActiveMethod] = useState(activeMetricMethod);
    const [key, setKey] = useState<number>(0);
    const [selectedLocationCords, setSelectedLocationCords] = useState<{
        lat: number;
        lng: number;
    }>({ lat: selectedLocationLat, lng: selectedLocationLng });

    const [ShowBlackSnackbar, setShowBlackSnackbar] = useState<boolean>(false);
    useEffect(() => {
        (window as any).dataLayer.push({
            event: "CropStage",
            pageUrl: "https://multimatch.haifa-group.com/multi-match/agri-solution/crop",
            pageTitle: "CropStage",
        });
    }, []);
    const {
        handleSubmit,
        control,
        register,
        setValue,
        getValues,
        watch,
        trigger,
        formState: { errors },
    } = useForm<IFormInputs>({
        mode: "onBlur",
        defaultValues: {
            expectedYield:
                metricUnit === activeMetricMethod
                    ? expectedYield
                    : activeMetricMethod == "1"
                    ? expectedYield
                        ? CalculateUtils.yieldToMetric(expectedYield ? expectedYield : 0)
                        : undefined
                    : expectedYield
                    ? CalculateUtils.yieldToImperial(expectedYield ? expectedYield : 0)
                    : undefined,
            plantingDate,
            finalHarvestDate,
            selectedLocationCountryCode,
        },
    });
    const theme = useTheme();
    const formResult = useWatch({ control });
    const [hasSaveButton, setHasSaveButton] = useState<boolean>(false);
    const [hasClearButton, setHasClearButton] = useState<boolean>(false);
    const [canSubmit, setCanSubmit] = useState<boolean>(false);

    const [showClearStepModal, setShowClearStepModal] = useState<boolean>(false);
    const [showSaveItemModal, setShowSaveItemModal] = useState<boolean>(false);
    const [cropsHasValue, setCropsHasValue] = useState<boolean>(false);

    const showClearThisStep = useSelector(GeneralState.Selectors.selectShowClearThisStepModal);

    const isUserSave = useSelector(AgriSolutionState.Selectors.selectIsUserSave);

    const plantingDateDefaultValue = TimeUtils.getDateFormatted(TimeUtils.getDate(0));

    const isCarbonBannerShown = useSelector(AgriSolutionState.Selectors.isCarbonBannerShown);

    const harvestDateDefaultValue = TimeUtils.getDateFormatted(TimeUtils.getDate(daysToJumpForFinalDate));
    /* Check buttons state based on form values */
    useEffect(() => {
        const { selectedCrop, expectedYield, selectedLocationCountryCode, plantingDate, finalHarvestDate } = formResult;

        const allHasValues = !!selectedCrop && !!expectedYield && !!plantingDate && !!finalHarvestDate && !!selectedLocationCountryCode;

        const anyHasValues = !!selectedCrop || !!expectedYield;

        const cropsWithValue = !!selectedCrop;

        setCropsHasValue(cropsWithValue);
        setCanSubmit(allHasValues);
        setHasClearButton(anyHasValues);
        setHasSaveButton(allHasValues);
        const { lat, lng } = selectedLocationCords;

        dispatch(
            AgriSolutionState.Actions.setSelectedCropsState({
                selectedCrop,
                expectedYield,
                plantingDate,
                finalHarvestDate,
                selectedLocationCountryCode,
                selectedLocationLat: lat,
                selectedLocationLng: lng,
                metricUnit: activeMetricMethod,
            })
        );
        () => {
            return;
        };
    }, [formResult]);

    // Reset yields value on measurement units update
    useEffect(() => {
        if (lastKnowActiveMethod !== activeMetricMethod) {
            setLastKnownActiveMethod(activeMetricMethod);
            const currentValue = getValues("expectedYield") ? getValues("expectedYield") : 0;
            const updatedValue =
                activeMetricMethod === "1"
                    ? currentValue
                        ? CalculateUtils.yieldToMetric(currentValue)
                        : undefined
                    : currentValue
                    ? CalculateUtils.yieldToImperial(currentValue)
                    : undefined;

            setValue("expectedYield", updatedValue);
        }
    }, [activeMetricMethod, lastKnowActiveMethod]);

    useEffect(() => {
        selectedCrop && setValue("selectedCrop", selectedCrop);
    }, [selectedCrop, setValue]);

    const saveSolutionHandler = async () => {
        if (!greenBar.isShow) {
            await dispatch(AgriSolutionState.Actions.setIsUserSave(true));
            await dispatch(
                AgriSolutionState.Thunks.saveCropStage(() => {
                    //
                })
            );
        }
        if (haveItemId) {
            setShowBlackSnackbar(true);
        }
    };

    const handleOnSubmit: SubmitHandler<IFormInputs> = async () => {
        if (!greenBar.isShow) {
            await dispatch(
                AgriSolutionState.Thunks.saveCropStage(() => {
                    history.push(LOGGED_IN_ROUTES.AGRI_SOLUTION_SECOND_STEP);
                })
            );
        } else {
            dispatch(AgriSolutionState.Actions.setSoilStateCanBeNavigated(true));
            history.push(LOGGED_IN_ROUTES.AGRI_SOLUTION_SECOND_STEP);
            await dispatch(updateCropStateTemperature());
        }
    };
    const handleClear = () => {
        setValue("selectedCrop", undefined as any);
        setValue("expectedYield", undefined);
        setValue("selectedLocationCountryCode", "");
        setKey((currentKey) => currentKey + 1);
    };

    const handleFinalHarvestDateValidation = (value: string) => {
        const dateTo = value === undefined ? harvestDateDefaultValue : value;
        const isValid = ValidationUtils.validateDate(dateTo);
        if (!isValid) {
            return t("validationErrors.invalidDateFormat");
        }

        const dateFrom = watch("plantingDate") || TimeUtils.getDateFormatted(TimeUtils.getDate());

        const dateFromIsValid = ValidationUtils.validateDate(dateFrom);
        /* Validate that the date is bigger than date from*/
        if (dateFromIsValid && !TimeUtils.dateIsBefore(dateFrom, dateTo)) {
            return t("validationErrors.dateMustBeGreater");
        }

        if (dateFromIsValid && !TimeUtils.isAtLeastTwoMonth(dateFrom, dateTo)) {
            return t("validationErrors.dateMustBeAtLeastTwoMonths");
        }

        if (dateFromIsValid && !TimeUtils.isMaxTwelve(dateFrom, dateTo)) {
            return t("validationErrors.dateMaxDatesTwelveMonth");
        }

        return undefined;
    };

    const validateCountryCode = (isoCode: string) => {
        const countryHasProduct = !!isoCode && countriesWithHaifaProducts && !!countriesWithHaifaProducts.find((country) => country.code === isoCode);

        return countryHasProduct ? undefined : t("validationErrors.noServiceForSpecificCountry");
    };

    useEffect(() => {
        if (plantingDate === undefined) {
            setValue("plantingDate", plantingDateDefaultValue);
        }
    }, [plantingDate]);

    useEffect(() => {
        if (finalHarvestDate === undefined) {
            setValue("finalHarvestDate", harvestDateDefaultValue);
        }
    }, [finalHarvestDate]);

    const handleShowMobileMap = (show: boolean) => dispatch(AgriSolutionState.Actions.setShowMobileMap(show));

    //------------ Modals ---------------------//
    const closeClearModalHandle = () => {
        setShowClearStepModal(false);
    };
    const openClearModalHandle = () => {
        setShowClearStepModal(true);
    };
    const closeSaveItemModalHandle = () => {
        setShowSaveItemModal(false);
    };
    const openSaveItemModalHandle = () => {
        setShowSaveItemModal(true);
    };
    //-----------------------------------------//

    const closeSnackbarHandler = () => {
        setShowBlackSnackbar(false);
    };
    return (
        <>
            {ShowBlackSnackbar && (
                <div className={classes.snackbar_container}>
                    <FmBlackPopup text={t("saveItemPopup.snackbar")} snackbar={true} onClose={closeSnackbarHandler} />
                </div>
            )}
            <form
                onSubmit={handleSubmit(handleOnSubmit)}
                className={classNames(classes.container, {
                    [classes.positionBase]: isMobile && showMobileMap,
                })}
            >
                <div className={classes.title_container}>
                    <FmPageTitle title={t("steps.cropsMobile")} />
                    <FmUnderlineNav
                        title={t("saveItemPopup.save")}
                        onClick={
                            hasSaveButton && !isUserSave
                                ? openSaveItemModalHandle
                                : hasSaveButton && isUserSave
                                ? saveSolutionHandler
                                : () => {
                                      //
                                  }
                        }
                        disabled={!hasSaveButton}
                    />
                </div>
                {isCarbonBannerShown && (
                    //   <FmDescriptionSnippet
                    //     className={classes.snippetContainer}
                    //     onClose={() => setShowSnippet(false)}
                    //     title={t('fmDescriptionSnippet.title')}
                    //     description={t('fmDescriptionSnippet.description')}
                    //   />

                    <FmCarbonFootprintBanner
                        title='Carbon & Environmental footprint'
                        description='assessment tool is now included in MultiMatch to help you improve your environmental impact'
                        buttonText='Read More'
                        onClick={() => window.open(Config.cfpBannerLink, "_blank")}
                        onClose={() => dispatch(AgriSolutionState.Actions.hideCarbonBanner())}
                    />
                )}
                <div className={classes.noteContainer}>
                    <FmText variant={"text"}>{t("pageStep1.note")}</FmText>
                </div>
                <div className={classes.content}>
                    <div className={classes.leftSide}>
                        <div className={classes.inputContainer}>
                            <Controller
                                name={"selectedCrop"}
                                key={`selectedCrop-${key}`}
                                control={control}
                                rules={{
                                    required: {
                                        value: true,
                                        message: t("validationErrors.fieldIsRequired"),
                                    },
                                }}
                                render={({ field }) => {
                                    return (
                                        <FmAutocompleteInput
                                            defaultValue={(!cropsHasValue && crops.find((c) => c.code === selectedCrop)) || undefined}
                                            {...register("selectedCrop")}
                                            {...field}
                                            label={t("agriSolution.selectCropLabel")}
                                            error={errors.selectedCrop}
                                            helperText={errors.selectedCrop?.message || ""}
                                            data={crops}
                                            getLabel={(crop: any) => t(`crops.${crop.code}`)}
                                            getImageUrl={(crop: any) => `${Configs.cropImagesPath}/${crop.image}` || Configs.cropPlaceHolderImage}
                                            setValue={(crop: any) => {
                                                setValue("selectedCrop", crop && crop.code ? crop.code : undefined);
                                                // setCrop(crop && crop.code ? crop.code : undefined);
                                            }}
                                        />
                                    );
                                }}
                            />
                        </div>
                        <div className={classNames(classes.inputContainer, classes.inputTopMargin)}>
                            <Controller
                                name={"expectedYield"}
                                key={`expectedYield-${key}`}
                                control={control}
                                rules={{
                                    required: {
                                        value: true,
                                        message: t("validationErrors.fieldIsRequired"),
                                    },
                                    validate: (value: number | undefined) => {
                                        const min = (YIELD_MIN as any)[activeMetricMethod];
                                        const max = (YIELD_MAX as any)[activeMetricMethod];
                                        const isValid = value ? !isNaN(value) && value >= min && value <= max : false;
                                        return !isValid ? t("validationErrors.valueOutOfRange").replace("{0}", min).replace("{1}", max) : undefined;
                                    },
                                }}
                                render={({ field }) => {
                                    return (
                                        <FmTextField
                                            {...register("expectedYield")}
                                            {...field}
                                            type={"number"}
                                            label={`${t("agriSolution.expectedYieldLabel")} ${(BIG_MESURMENT_UNIT as any)[activeMetricMethod]}`}
                                            variant='filled'
                                            error={errors.expectedYield}
                                            value={field.value}
                                            helperText={errors.expectedYield?.message || ""}
                                        />
                                    );
                                }}
                            />
                        </div>
                        <div className={classNames(classes.inputContainer, classes.inputTopMargin)}>
                            <Controller
                                name={"plantingDate"}
                                key={`plantingDate-${key}`}
                                control={control}
                                rules={{
                                    required: {
                                        value: true,
                                        message: t("validationErrors.fieldIsRequired"),
                                    },
                                    validate: (value: string) => {
                                        const isValid = ValidationUtils.validateDate(value);

                                        return (!isValid ? t("validationErrors.invalidDateFormat") : undefined) as any;
                                    },
                                }}
                                render={({ field }) => {
                                    return (
                                        <FmDatePicker
                                            {...register("plantingDate")}
                                            {...field}
                                            label={t("agriSolution.plantingDateLabel")}
                                            variant='filled'
                                            error={errors.plantingDate}
                                            helperText={errors.plantingDate?.message || ""}
                                            defaultValue={plantingDateDefaultValue}
                                            value={field.value}
                                            setValue={(newDate: any) => {
                                                setValue("plantingDate", newDate, {
                                                    shouldValidate: true,
                                                    shouldDirty: true,
                                                });
                                                trigger("finalHarvestDate");
                                            }}
                                        />
                                    );
                                }}
                            />
                        </div>
                        <div className={classNames(classes.inputContainer, classes.inputTopMargin)}>
                            <Controller
                                name={"finalHarvestDate"}
                                key={`finalHarvestDate-${key}`}
                                control={control}
                                rules={{
                                    required: {
                                        value: true,
                                        message: t("validationErrors.fieldIsRequired"),
                                    },
                                    validate: (value: string) => handleFinalHarvestDateValidation(value),
                                }}
                                render={({ field }) => {
                                    return (
                                        <FmDatePicker
                                            {...register("finalHarvestDate")}
                                            {...field}
                                            label={t("agriSolution.finalHarvestExpectedDate")}
                                            variant='filled'
                                            error={errors.finalHarvestDate}
                                            helperText={errors.finalHarvestDate?.message || ""}
                                            defaultValue={harvestDateDefaultValue}
                                            value={field.value}
                                            setValue={(newDate: any) => {
                                                setValue("finalHarvestDate", newDate, {
                                                    shouldValidate: true,
                                                    shouldDirty: true,
                                                });
                                            }}
                                        />
                                    );
                                }}
                            />
                        </div>
                    </div>
                    <div className={classes.rightSide}>
                        <Controller
                            name={"selectedLocationCountryCode"}
                            key={`selectedLocationCountryCode-${key}`}
                            control={control}
                            rules={{
                                required: {
                                    value: true,
                                    message: t("validationErrors.fieldIsRequired"),
                                },
                                validate: (value: string) => validateCountryCode(value),
                            }}
                            render={({ field }) => {
                                return (
                                    <FmMapInput
                                        showMobileMap={showMobileMap}
                                        setShowMobileMap={handleShowMobileMap}
                                        helperText={errors.selectedLocationCountryCode?.message || ""}
                                        value={field.value}
                                        label={t("agriSolution.selectPlotLocationLabel")}
                                        setValue={(isoCode: string, lat: number, lng: number) => {
                                            setSelectedLocationCords({ lat, lng });

                                            setValue("selectedLocationCountryCode", isoCode, {
                                                shouldValidate: true,
                                                shouldDirty: true,
                                            });
                                        }}
                                        error={errors.selectedLocationCountryCode}
                                    />
                                );
                            }}
                        />
                    </div>
                </div>
                <div className={classes.footer} style={{ marginBottom: "2rem" }}>
                    <BottomSection
                        onClear={hasClearButton ? (showClearThisStep ? openClearModalHandle : handleClear) : undefined}
                        canSubmit={canSubmit}
                        loading={loading}
                        clearButtonIsDisabled={!hasClearButton}
                    />
                </div>
            </form>
            <FmAllModals
                clearModalIsOpen={showClearStepModal}
                clearModalOnClose={closeClearModalHandle}
                clearModalAction={handleClear}
                saveItemModalIsOpen={showSaveItemModal}
                saveItemModalOnClose={closeSaveItemModalHandle}
                saveItemModalAction={saveSolutionHandler}
                SaveProcessPromptWhen={hasSaveButton}
                SaveProcessPromptMessage={t("saveProcessPopup.subTitle")}
            />
        </>
    );
};

export default CropStagePage;
